/**
 * Social-login return page.
 *
 * Coderblock's OAuth relay redirects here after Google/GitHub consent with the
 * Supabase session in the URL fragment. We apply it (`supabase.auth.setSession`)
 * and bounce home. Do NOT link to this page — it's the OAuth callback target.
 *
 * The route path is fixed to `/auth/social/complete` (the relay's return path).
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { completeSocialLogin } from '@/integrations/coderblock/auth'
import { supabase } from '@/integrations/supabase/client'

export const route = { path: 'auth/social/complete' }

export default function AuthSocialCompletePage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    completeSocialLogin().then(async (result) => {
      if (!active) return
      if (!result.ok) {
        setError(result.error || 'Sign-in failed')
        return
      }
      // New social users haven't picked a username yet → send them to onboarding.
      const { data: { user } } = await supabase.auth.getUser()
      if (!active) return
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('username')
          .eq('id', user.id)
          .maybeSingle()
        if (!active) return
        if (!profile?.username) {
          navigate('/onboarding', { replace: true })
          return
        }
      }
      navigate('/dashboard', { replace: true })
    })
    return () => {
      active = false
    }
  }, [navigate])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="glass-strong rounded-3xl p-8 sm:p-10 w-full max-w-sm flex flex-col items-center gap-4 text-center">
        {error ? (
          <>
            <p className="text-lg font-semibold text-neutral-900">Sign-in failed</p>
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="mt-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-neutral-800 transition-colors"
            >
              Back to app
            </button>
          </>
        ) : (
          <>
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900" />
            <p className="text-sm text-muted-foreground">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  )
}
